import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Globe } from "lucide-react";
import { BRAND, CONTACT } from "../data/site";
import SocialLinks from "./SocialLinks";

const LOGO = "/assets/ats-logo.png";

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Case Studies", to: "/#work" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
  { label: "Privacy Policy", to: "/privacy" },
];

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="relative bg-navy-deep border-t border-white/10 pt-20 pb-10">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <img src={LOGO} alt={`${BRAND.name} logo`} className="h-20 w-20 rounded-full object-contain mb-6 shadow-lg shadow-black/30" />
            <p data-testid="footer-brand-name" className="font-head text-white font-semibold text-lg">{BRAND.name}</p>
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm mt-2">
              Engineering reliable technology — IT consulting, AI, cloud and full-stack delivery for businesses that take engineering seriously.
            </p>
            <p data-testid="footer-brand-legal" className="text-slate-500 text-xs mt-4">
              {BRAND.name} is a brand of {BRAND.legal}
            </p>
            <div className="mt-6">
              <SocialLinks prefix="footer" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-head text-white font-semibold mb-5 text-sm tracking-wide">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="text-slate-400 text-sm hover:text-brand-orange transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-head text-white font-semibold mb-5 text-sm tracking-wide">Get in Touch</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a href={`tel:${CONTACT.phone}`} data-testid="footer-phone" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors">
                  <Phone size={16} className="mt-0.5 text-brand-orange shrink-0" /> {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors break-all">
                  <Mail size={16} className="mt-0.5 text-brand-orange shrink-0" />
                  <span><span className="block font-mono text-[10px] tracking-wider uppercase text-slate-500">Primary Email</span>{CONTACT.email}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.businessEmail}`} data-testid="footer-business-email" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors break-all">
                  <Mail size={16} className="mt-0.5 text-brand-orange shrink-0" />
                  <span><span className="block font-mono text-[10px] tracking-wider uppercase text-slate-500">Business Enquiries</span>{CONTACT.businessEmail}</span>
                </a>
              </li>
              <li>
                <a href={BRAND.url} data-testid="footer-website" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors">
                  <Globe size={16} className="mt-0.5 text-brand-orange shrink-0" /> {BRAND.domain}
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin size={16} className="mt-0.5 text-brand-orange shrink-0" /> {CONTACT.address}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p data-testid="footer-copyright" className="text-slate-500 text-xs">
            © {new Date().getFullYear()} {BRAND.legal} All rights reserved.
          </p>
          <p className="text-slate-500 text-xs font-mono">{BRAND.domain}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
